'use client';

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useGetDataIssueAddressSuggestionsQuery as useGetDataIssueAddressSuggestions } from 'src/api/redux';
import { DataIssueAddressSearch } from './DataIssueAddressSearch';
import { AddressType } from './types';
import { Box, CircularProgress, Chip, PulseRadio } from 'src/components/PulseUI';
import { AddressSuggestionItem } from 'src/api';
import { twMerge } from 'tailwind-merge';

interface SuggestionItemProps {
  item: AddressSuggestionItem & { suggestionType: 'address' | 'other' };
  isSelected: boolean;
  onSelect: (value: string) => void;
}

const SuggestionItem = ({ item, isSelected, onSelect }: SuggestionItemProps) => {
  const itemValue = item.address_key || item.id;
  const testId =
    item.suggestionType === 'address'
      ? `address-suggestion-${item.address_key}`
      : 'address-suggestion-other';

  return (
    <div
      className={twMerge(
        'relative flex items-start gap-3 p-4 rounded-lg border cursor-pointer shadow-sm transition-all',
        isSelected
          ? 'border-2 border-sky-500'
          : 'border border-neutral-200 hover:border-neutral-300'
      )}
      onClick={() => onSelect(String(itemValue))}
      data-testid={testId}
    >
      <div className="mt-0.5 shrink-0">
        <PulseRadio
          label=""
          name="suggestion"
          value={String(itemValue)}
          checked={isSelected}
          onChange={() => onSelect(String(itemValue))}
          aria-label={
            item.suggestionType === 'address'
              ? `Select address ${item.address_line_1}`
              : 'Select other option'
          }
          data-testid={`${testId}-radio`}
        />
      </div>
      <div className="flex-1 min-w-0">
        {item.suggestionType === 'address' ? (
          <AddressSuggestion addressObject={item} />
        ) : (
          <OtherSuggestion />
        )}
      </div>
    </div>
  );
};

interface AddressSuggestionProps {
  addressObject: AddressSuggestionItem;
}

const AddressSuggestion = ({ addressObject }: AddressSuggestionProps) => {
  return (
    <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-700">
          {addressObject?.practice_name}
        </span>
        <span className="text-sm font-medium text-gray-700">
          {addressObject?.address_line_1}
        </span>
        <span className="text-xs text-neutral-500">
          {addressObject?.city}, {addressObject?.state} {addressObject?.zip_code}{' '}
          {addressObject?.address_line_2 && addressObject?.address_line_2}
        </span>
      </div>
      {addressObject?.is_primary && (
        <div className="mt-1 md:mt-0">
          <Chip variant="soft" color="primary" size="sm">
            Current Address
          </Chip>
        </div>
      )}
    </div>
  );
};

const OtherSuggestion = () => {
  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-gray-700">Other</span>
      <span className="text-xs text-neutral-500">Please specify below</span>
    </div>
  );
};

interface DataIssueAddressSuggestionsProps {
  providerId: string;
  dataIssueAddressKey?: string;
  setAddressData: (value: AddressType | null) => void;
  initialAddressData?: AddressType | null;
}

const DataIssueAddressSuggestions = ({
  providerId,
  dataIssueAddressKey,
  setAddressData,
  initialAddressData,
}: DataIssueAddressSuggestionsProps) => {
  const { data, isLoading } = useGetDataIssueAddressSuggestions({
    providerId,
  });

  const suggestionResults = useMemo(() => {
    return data?.results || [];
  }, [data]);

  const [suggestedAddresses, setSuggestedAddresses] =
    useState<AddressSuggestionItem[]>(suggestionResults);
  const [addressId, setAddressId] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const initialAddressDataRef = useRef(initialAddressData);
  initialAddressDataRef.current = initialAddressData;

  const setAddressDataRef = useRef(setAddressData);
  setAddressDataRef.current = setAddressData;

  useEffect(() => {
    if (isLoading) return;

    const matchedSuggestion = dataIssueAddressKey
      ? suggestionResults.find(
          (a) =>
            String(a.id) === String(dataIssueAddressKey) ||
            a.address_key === dataIssueAddressKey
        )
      : null;

    if (matchedSuggestion) {
      setSuggestedAddresses(suggestionResults);
      setAddressId(
        matchedSuggestion.address_key || String(matchedSuggestion.id)
      );
    } else if (dataIssueAddressKey && initialAddressDataRef.current) {
      const savedAddress = initialAddressDataRef.current;
      const restoredAddress = {
        ...savedAddress,
        id: savedAddress.address_key,
        suggestionType: 'address' as const,
      };
      setSuggestedAddresses([...suggestionResults, restoredAddress]);
      setAddressId(String(restoredAddress.id));
      setAddressDataRef.current(savedAddress);
    } else {
      setSuggestedAddresses(suggestionResults);
      setAddressId(null);
    }

    setIsInitialized(true);
  }, [isLoading, suggestionResults, dataIssueAddressKey]);

  const handleChange = useCallback(
    (selectionId: string | null) => {
      if (selectionId === null) {
        setAddressId(null);
        setAddressData(null);
        return;
      }

      let selectedAddress: AddressSuggestionItem | undefined = undefined;

      if (selectionId !== 'other') {
        selectedAddress = suggestedAddresses.find(
          (item) =>
            String(item.id) === String(selectionId) ||
            item.address_key === selectionId
        );
      }

      setAddressId(selectionId);

      if (selectedAddress) {
        setAddressData({
          address_line_1: selectedAddress.address_line_1,
          address_line_2: selectedAddress.address_line_2,
          city: selectedAddress.city,
          state: selectedAddress.state,
          zip_code: selectedAddress.zip_code,
          lat: selectedAddress.lat,
          lng: selectedAddress.lng,
          address_key:
            selectedAddress.address_key || String(selectedAddress.id),
        } as AddressType);
      } else {
        setAddressData(null);
      }
    },
    [suggestedAddresses, setAddressData]
  );

  const setAdditionalAddress = useCallback(
    (addressDataFromSearch: any) => {
      if (!addressDataFromSearch) {
        return;
      }

      const newAddressObject = {
        ...addressDataFromSearch,
        id: addressDataFromSearch.address_key || `search_${Date.now()}`,
        suggestionType: 'address',
      };

      setSuggestedAddresses((prevAddresses) => [
        ...prevAddresses,
        newAddressObject,
      ]);

      const newAddressSelectionId = newAddressObject.id;
      setAddressId(newAddressSelectionId);

      setAddressData({
        address_line_1: newAddressObject.address_line_1,
        address_line_2: newAddressObject.address_line_2,
        city: newAddressObject.city,
        state: newAddressObject.state,
        zip_code: newAddressObject.zip_code,
        lat: newAddressObject.lat,
        lng: newAddressObject.lng,
        address_key: newAddressObject.address_key || newAddressSelectionId,
      } as AddressType);
    },
    [setAddressData]
  );

  if (!isInitialized) {
    return (
      <Box
        className="flex justify-center items-center h-full pt-4"
        data-testid="data-issue-address-suggestions-loading-indicator"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div data-testid="data-issue-address-suggestions">
      <div className="text-sm font-medium">Select or enter a correction:</div>
      <div
        role="radiogroup"
        aria-label="Address suggestions"
        className="flex flex-col gap-2 min-w-60"
        data-testid="address-suggestions-radiogroup"
      >
        {suggestedAddresses.map((item) => (
          <SuggestionItem
            key={item.address_key}
            item={{ ...item, suggestionType: 'address' }}
            isSelected={
              addressId === item.address_key || addressId === String(item.id)
            }
            onSelect={handleChange}
          />
        ))}
        <SuggestionItem
          key="other"
          item={{
            id: 'other',
            suggestionType: 'other',
            address_key: 'other',
          } as AddressSuggestionItem & { suggestionType: 'other' }}
          isSelected={addressId === 'other'}
          onSelect={handleChange}
        />
      </div>
      {addressId === 'other' && (
        <DataIssueAddressSearch setAddressData={setAdditionalAddress} />
      )}
    </div>
  );
};

export { DataIssueAddressSuggestions };
